import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { config } from "../config";
import { AUTHORIZE, LOGOUT, SCOPES, exchangeCode, type IdTokenClaims } from "./cognito";
import { challengeFromVerifier, decodeJwt, randomState, randomVerifier } from "./pkce";

const PKCE_KEY = "tower.pkce";
const SESSION_KEY = "tower.session";

interface Session {
  idToken: string;
  expiresAt: number;
}

interface AuthContextValue {
  user: IdTokenClaims | null;
  idToken: string | null;
  loading: boolean;
  error: string | null;
  login: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function loadSession(): Session | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as Session;
    if (session.expiresAt <= Date.now()) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(loadSession);
  const [loading, setLoading] = useState(window.location.pathname === "/callback");
  const [error, setError] = useState<string | null>(null);
  const callbackHandled = useRef(false);

  useEffect(() => {
    if (window.location.pathname !== "/callback" || callbackHandled.current) return;
    callbackHandled.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");

    const finish = (err: string | null) => {
      setError(err);
      setLoading(false);
      window.history.replaceState(null, "", "/");
    };

    const stored = sessionStorage.getItem(PKCE_KEY);
    sessionStorage.removeItem(PKCE_KEY);
    if (!code || !stored) {
      finish(params.get("error_description") ?? "login was cancelled or invalid");
      return;
    }
    const pkce = JSON.parse(stored) as { verifier: string; state: string };
    if (state !== pkce.state) {
      finish("state mismatch - possible CSRF, login aborted");
      return;
    }

    exchangeCode(code, pkce.verifier)
      .then((tokens) => {
        const claims = decodeJwt<IdTokenClaims>(tokens.id_token);
        if (claims.aud !== config.cognitoClientId) throw new Error("unexpected token audience");
        if (claims.exp * 1000 <= Date.now()) throw new Error("token already expired");
        const newSession: Session = { idToken: tokens.id_token, expiresAt: claims.exp * 1000 };
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(newSession));
        setSession(newSession);
        finish(null);
      })
      .catch((e: Error) => finish(e.message));
  }, []);

  const login = useCallback(async () => {
    const verifier = randomVerifier();
    const state = randomState();
    sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state }));
    const challenge = await challengeFromVerifier(verifier);
    const url = new URL(AUTHORIZE);
    url.search = new URLSearchParams({
      client_id: config.cognitoClientId,
      response_type: "code",
      scope: SCOPES,
      redirect_uri: config.redirectUri,
      state,
      code_challenge: challenge,
      code_challenge_method: "S256",
      identity_provider: "Google",
    }).toString();
    window.location.assign(url.toString());
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    setSession(null);
    const url = new URL(LOGOUT);
    url.search = new URLSearchParams({
      client_id: config.cognitoClientId,
      logout_uri: `${config.siteUrl}/`,
    }).toString();
    window.location.assign(url.toString());
  }, []);

  const user = session ? decodeJwt<IdTokenClaims>(session.idToken) : null;

  return (
    <AuthContext.Provider
      value={{ user, idToken: session?.idToken ?? null, loading, error, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
